import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";

import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { isFreePlan } from "@/components/shared/PaidPlanRoute";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import {
  SettingsDocumentsCard,
  type SettingsDocumentsCardRef,
} from "@/pages/settings/SettingsDocumentsCard";

export function DocumentsPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const cardRef = useRef<SettingsDocumentsCardRef>(null);
  const [uploading, setUploading] = useState(false);

  const { data: currentTenant, isLoading: tenantLoading } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const hasPaidPlanAccess = !isFreePlan(currentTenant?.planId);

  const handleAddClick = useCallback(() => {
    cardRef.current?.openFilePicker();
  }, []);

  if (tenantLoading) {
    return <LoadingPage />;
  }

  if (!hasPaidPlanAccess) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader
          eyebrow={t("documents.eyebrow", "Archivos")}
          title={t("settings.documents.title")}
          description={t("settings.documents.description")}
        />
        <div
          className="min-h-[32vh] rounded-lg border border-dashed border-border/60 bg-muted/20"
          aria-hidden
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("documents.eyebrow", "Archivos")}
        title={t("settings.documents.title")}
        description={t("settings.documents.description")}
        actions={
          <Button
            type="button"
            size="sm"
            onClick={handleAddClick}
            disabled={uploading}
            className="transition-colors duration-150"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("settings.documents.uploading")}
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                {t("settings.documents.add")}
              </>
            )}
          </Button>
        }
      />
      <SettingsDocumentsCard
        ref={cardRef}
        onUploadingChange={setUploading}
      />
    </div>
  );
}
